// Local candle cache (D-0xx: the historical-data storage layer).
//
// THE DATA PROBLEM: bundling every security's history into the extension
// package bloats the install and slows the service worker (the 62 MB XAU
// bundle already hiccuped registration). It does not scale to many symbols
// or many years. THE SOLUTION here: ship CODE, not data. Candles live in an
// on-disk IndexedDB cache (the extension holds the `unlimitedStorage`
// permission), populated lazily from a pluggable `CandleSource`. The package
// carries zero market data.
//
// Design choices that keep it fast and un-bloated no matter how much a user
// piles in:
//   * ONE base series per symbol. We persist only 1-minute (the finest) plus
//     the tiny native daily/weekly; 5m/15m/30m/1h are resampled from 1m on
//     read. One heavy series instead of seven.
//   * Monthly chunks. Each (symbol, timeframe, "YYYY-MM") is a row, so reads
//     touch only the months in view and writes never rewrite a whole series.
//   * LRU size cap. When the cache exceeds its byte budget the least-recently
//     read 1-minute chunks are evicted (the cheap daily/weekly stay). This is
//     the piece that stops sluggishness as securities accumulate.
//   * Pluggable source. The chart always reads from the cache; a cache miss
//     asks the configured `CandleSource` for the month. Today that source is
//     OANDA (the founder's broker feed; see oanda-source.ts); for real users it
//     swaps to the backend data hub — same store, same code path.

export interface RawBar {
  t: string;
  o: number;
  h: number;
  l: number;
  c: number;
  v?: number;
}

/** The per-month payload a source hands us: native bars keyed by timeframe.
 * We only persist `PERSIST_TFS`; anything else is ignored (derived on read). */
export interface MonthBars {
  bars: Record<string, RawBar[]>;
}

/** A place candle months can be fetched from on a cache miss. Implementations:
 * a local folder import (now) and the backend data hub (later) — identical
 * store, only the bytes' origin differs. */
export interface CandleSource {
  /** Month keys ("YYYY-MM") this source can serve for the symbol, if known. */
  availableMonths(symbol: string): Promise<string[]>;
  /** One month's native bars, or null when the source hasn't got it. */
  fetchMonth(symbol: string, month: string): Promise<MonthBars | null>;
}

const DB_NAME = "tickahead-candles";
const DB_VERSION = 1;
const CHUNK_STORE = "chunks";
const LASTACCESS_INDEX = "lastAccess";

/** The timeframes we actually persist. 5m/15m/30m resample from 1m at read time
 * (short windows); hourly and daily are kept native so long-history views don't
 * read millions of 1-minute bars. Weekly is derived from the daily series on
 * read (below) — bucketing it per stored month would split the boundary week. */
export const PERSIST_TFS = ["1m", "1h", "1d"] as const;
/** The canonical stored series a (non-weekly) timeframe reads from. */
function canonicalTf(tf: string): "1m" | "1h" | "1d" {
  const k = tf.trim().toLowerCase();
  if (k === "1d") return "1d";
  if (k === "1h" || k === "60m") return "1h";
  return "1m";
}
function isWeekly(tf: string): boolean {
  const k = tf.trim().toLowerCase();
  return k === "1w" || k === "1wk";
}
/** Bucket size (ms) for resampling 1m up to a coarser intraday frame.
 * Only the sub-hour frames resample; 1h is stored natively (see PERSIST_TFS). */
function intradayBucketMs(tf: string): number | null {
  switch (tf.trim().toLowerCase()) {
    case "5m": return 5 * 60_000;
    case "15m": return 15 * 60_000;
    case "30m": return 30 * 60_000;
    default: return null; // 1m / 1h (no resample) or a canonical frame
  }
}

/** Default cache budget. Generous — 1m XAU is ~3 MB/month, so this holds
 * ~20 years of a symbol, or a broad basket, before anything is evicted. */
const DEFAULT_BUDGET_BYTES = 750 * 1024 * 1024;

interface ChunkRecord {
  id: string; // `${symbol}|${tf}|${month}`
  symbol: string;
  tf: string;
  month: string; // YYYY-MM
  bars: RawBar[];
  bytes: number;
  lastAccess: number;
}

function chunkId(symbol: string, tf: string, month: string): string {
  return `${symbol.toUpperCase()}|${tf}|${month}`;
}
function estimateBytes(bars: RawBar[]): number {
  // Cheap, stable proxy for on-disk cost (avoids JSON.stringify of huge arrays).
  return bars.length * 48;
}

let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CHUNK_STORE)) {
        const store = db.createObjectStore(CHUNK_STORE, { keyPath: "id" });
        store.createIndex(LASTACCESS_INDEX, "lastAccess");
        store.createIndex("symbol", "symbol");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(db: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(CHUNK_STORE, mode).objectStore(CHUNK_STORE);
}
function reqDone<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// --- month helpers -----------------------------------------------------------

/** Inclusive list of "YYYY-MM" between two epoch-ms bounds. */
export function monthsBetween(fromMs: number, toMs: number): string[] {
  const out: string[] = [];
  const start = new Date(Date.UTC(new Date(fromMs).getUTCFullYear(), new Date(fromMs).getUTCMonth(), 1));
  const end = new Date(toMs);
  const cur = new Date(start);
  while (cur.getTime() <= end.getTime() && out.length < 600) {
    out.push(`${cur.getUTCFullYear()}-${String(cur.getUTCMonth() + 1).padStart(2, "0")}`);
    cur.setUTCMonth(cur.getUTCMonth() + 1);
  }
  return out;
}

// --- resampling --------------------------------------------------------------

/** Time-bucketed resample of 1-minute bars up to `bucketMs`. Buckets by
 * timestamp (not a fixed bar count), so weekend/session gaps never smear one
 * bar into the next — the failure mode of naive factor-of-N grouping. */
export function resampleByTime(bars: RawBar[], bucketMs: number): RawBar[] {
  const out: RawBar[] = [];
  let cur: RawBar | null = null;
  let curBucket = NaN;
  for (const b of bars) {
    const bucket = Math.floor(Date.parse(b.t) / bucketMs) * bucketMs;
    if (!cur || bucket !== curBucket) {
      if (cur) out.push(cur);
      curBucket = bucket;
      cur = { t: new Date(bucket).toISOString(), o: b.o, h: b.h, l: b.l, c: b.c, v: b.v ?? 0 };
    } else {
      cur.h = Math.max(cur.h, b.h);
      cur.l = Math.min(cur.l, b.l);
      cur.c = b.c;
      cur.v = (cur.v ?? 0) + (b.v ?? 0);
    }
  }
  if (cur) out.push(cur);
  return out;
}

/** Calendar-bucket a daily series into weekly candles (Monday-anchored, UTC).
 * Run over the WHOLE concatenated daily series so a week spanning a month
 * boundary is one bar, not two partials. */
export function bucketWeekly(days: RawBar[]): RawBar[] {
  const out: RawBar[] = [];
  let cur: RawBar | null = null;
  let curKey = "";
  for (const d of days) {
    const dt = new Date(d.t);
    const diff = (dt.getUTCDay() + 6) % 7; // days since Monday
    const monday = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate() - diff));
    const key = monday.toISOString();
    if (!cur || key !== curKey) {
      if (cur) out.push(cur);
      curKey = key;
      cur = { t: `${key.slice(0, 10)}T00:00:00+00:00`, o: d.o, h: d.h, l: d.l, c: d.c, v: d.v ?? 0 };
    } else {
      cur.h = Math.max(cur.h, d.h);
      cur.l = Math.min(cur.l, d.l);
      cur.c = d.c;
      cur.v = (cur.v ?? 0) + (d.v ?? 0);
    }
  }
  if (cur) out.push(cur);
  return out;
}

// --- the store ---------------------------------------------------------------

export class CandleCache {
  private budgetBytes = DEFAULT_BUDGET_BYTES;
  private source: CandleSource | null = null;

  /** Where cache misses are filled from (folder import now, hub later). */
  setSource(source: CandleSource | null): void {
    this.source = source;
  }
  setBudgetBytes(bytes: number): void {
    this.budgetBytes = bytes;
  }

  /** Persist one month's native bars for a symbol (1m + native daily/weekly).
   * Used by the importer and by lazy source fills. Runs eviction afterward. */
  async putMonth(symbol: string, month: string, payload: MonthBars): Promise<void> {
    const db = await openDb();
    const now = Date.now();
    const store = tx(db, "readwrite");
    for (const tf of PERSIST_TFS) {
      const bars = payload.bars[tf] ?? (tf === "1h" ? payload.bars["60m"] : undefined);
      if (!bars || !bars.length) continue;
      const rec: ChunkRecord = {
        id: chunkId(symbol, tf, month),
        symbol: symbol.toUpperCase(),
        tf,
        month,
        bars,
        bytes: estimateBytes(bars),
        lastAccess: now,
      };
      store.put(rec);
    }
    await new Promise<void>((resolve, reject) => {
      store.transaction.oncomplete = () => resolve();
      store.transaction.onerror = () => reject(store.transaction.error);
    });
    await this.evictIfNeeded();
  }

  /** True once at least one month is cached for the symbol. */
  async hasData(symbol: string): Promise<boolean> {
    const db = await openDb();
    const idx = tx(db, "readonly").index("symbol");
    const first = await reqDone(idx.openKeyCursor(IDBKeyRange.only(symbol.toUpperCase())));
    return first != null;
  }

  /** Months currently cached for a symbol (any timeframe), sorted. */
  async cachedMonths(symbol: string): Promise<string[]> {
    const db = await openDb();
    const idx = tx(db, "readonly").index("symbol");
    const recs = await reqDone(idx.getAll(IDBKeyRange.only(symbol.toUpperCase())));
    const months = new Set<string>();
    for (const r of recs as ChunkRecord[]) months.add(r.month);
    return [...months].sort();
  }

  /** Cache-wide stats for a status/settings readout. */
  async stats(): Promise<{ symbols: number; chunks: number; bytes: number }> {
    const db = await openDb();
    const recs = (await reqDone(tx(db, "readonly").getAll())) as ChunkRecord[];
    const symbols = new Set(recs.map((r) => r.symbol));
    let bytes = 0;
    for (const r of recs) bytes += r.bytes;
    return { symbols: symbols.size, chunks: recs.length, bytes };
  }

  private async readChunk(symbol: string, tf: string, month: string): Promise<RawBar[] | null> {
    const db = await openDb();
    const store = tx(db, "readwrite");
    const rec = (await reqDone(store.get(chunkId(symbol, tf, month)))) as ChunkRecord | undefined;
    if (!rec) return null;
    rec.lastAccess = Date.now(); // touch for LRU
    store.put(rec);
    return rec.bars;
  }

  /** The main read path: candles for a symbol/timeframe over [fromMs, toMs].
   * Misses are filled from the configured source (if any) then cached. Coarser
   * intraday frames are resampled from the 1-minute base. */
  async readCandles(symbol: string, tf: string, fromMs: number, toMs: number): Promise<RawBar[]> {
    const weekly = isWeekly(tf);
    const canon = weekly ? "1d" : canonicalTf(tf); // weekly reads the daily series
    const months = monthsBetween(fromMs, toMs);
    const collected: RawBar[] = [];
    for (const m of months) {
      let bars = await this.readChunk(symbol, canon, m);
      if (!bars && this.source) {
        const payload = await this.source.fetchMonth(symbol, m).catch(() => null);
        if (payload) {
          await this.putMonth(symbol, m, payload);
          bars = await this.readChunk(symbol, canon, m);
        }
      }
      // Fallback: a month cached before hourly was persisted natively has no 1h
      // chunk — derive it from that month's 1-minute base so nothing breaks and
      // no re-import is required.
      if (!bars && canon === "1h") {
        const m1 = await this.readChunk(symbol, "1m", m);
        if (m1) bars = resampleByTime(m1, 60 * 60_000);
      }
      if (bars) collected.push(...bars);
    }
    collected.sort((a, b) => a.t.localeCompare(b.t));

    const bucket = weekly ? null : intradayBucketMs(tf);
    let series = weekly ? bucketWeekly(collected) : bucket ? resampleByTime(collected, bucket) : collected;

    // Coarse frames (daily/weekly) return the whole months unclipped so those
    // charts are never near-empty; intraday (1m/1h and resampled) clips to the
    // requested window.
    if (canon !== "1d") {
      series = series.filter((b) => {
        const t = Date.parse(b.t);
        return t >= fromMs && t <= toMs;
      });
    }
    return series;
  }

  /** LRU trim: while over budget, drop the least-recently-read 1-minute chunks
   * (daily/weekly are tiny and kept). */
  private async evictIfNeeded(): Promise<void> {
    const db = await openDb();
    const all = (await reqDone(tx(db, "readonly").getAll())) as ChunkRecord[];
    let total = 0;
    for (const r of all) total += r.bytes;
    if (total <= this.budgetBytes) return;

    const evictable = all.filter((r) => r.tf === "1m").sort((a, b) => a.lastAccess - b.lastAccess);
    const store = tx(db, "readwrite");
    for (const r of evictable) {
      if (total <= this.budgetBytes) break;
      store.delete(r.id);
      total -= r.bytes;
    }
    await new Promise<void>((resolve) => {
      store.transaction.oncomplete = () => resolve();
      store.transaction.onerror = () => resolve();
    });
  }

  /** Wipe everything (a "clear cache" affordance). */
  async clear(): Promise<void> {
    const db = await openDb();
    const store = tx(db, "readwrite");
    store.clear();
    await new Promise<void>((resolve, reject) => {
      store.transaction.oncomplete = () => resolve();
      store.transaction.onerror = () => reject(store.transaction.error);
    });
  }

  /** Drop every cached chunk for one symbol — used before an OANDA reload so the
   * fresh feed fully REPLACES any older-source months rather than merging. */
  async clearSymbol(symbol: string): Promise<void> {
    const db = await openDb();
    const store = tx(db, "readwrite");
    const idx = store.index("symbol");
    const keys = (await reqDone(idx.getAllKeys(IDBKeyRange.only(symbol.toUpperCase())))) as IDBValidKey[];
    for (const k of keys) store.delete(k);
    await new Promise<void>((resolve, reject) => {
      store.transaction.oncomplete = () => resolve();
      store.transaction.onerror = () => reject(store.transaction.error);
    });
  }
}

/** The process-wide cache instance. */
export const candleCache = new CandleCache();
